import { default as React, useCallback, useEffect, useState } from 'react';
import { AlertColor } from '@mui/material';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import makeStyles from '@mui/styles/makeStyles';
import {
  GroupType,
  GroupTypeDetails,
  SmartSuggestionSettings,
  SmartSuggestionSettingsInput
} from 'graphql/types';
import { useMessage } from 'modules/intl';
import { useFeatureFlag } from 'modules/common/hooks/useFeatureFlag';
import {
  EnabledDisabled,
  SmartSuggestionSettingUseFieldName
} from './constant';
import { useGetSmartSuggestionDefaultValues } from './hooks/useGetDefaultValues';
import { usePutSmartSuggestionSettings } from './hooks/usePutSmartSuggestionSettings';
import { SettingBody } from './SettingBody';
import { mapToAccessSope } from './utils';

const useStyles = makeStyles(() => ({
  form: () => ({
    height: '100%'
  })
}));

type SmartSuggestionPage = {
  enabledGroupTypes: GroupTypeDetails[];
  settings: SmartSuggestionSettings[];
  onClose?: () => void;
  setAlert?: React.Dispatch<React.SetStateAction<boolean>> | undefined;
  setSeverity?: React.Dispatch<React.SetStateAction<AlertColor>>;
};

export const SmartSuggestionPage: React.FC<SmartSuggestionPage> = ({
  enabledGroupTypes,
  settings,
  onClose,
  setAlert,
  setSeverity
}: SmartSuggestionPage) => {
  const [show, setShow] = useState<boolean>(false);
  const category = 'success' as AlertColor;
  const message = useMessage('settings.smartSuggestionSaveSuccessMessage');
  const isTiAutoAcceptUserSettingsEnabled = useFeatureFlag(
    'isTiAutoAcceptUserSettingsEnabled'
  );
  const classes = useStyles();
  const fieldValues = useGetSmartSuggestionDefaultValues(settings);
  const formMethods = useForm(fieldValues);
  const { control, formState, reset, getValues } = formMethods;
  const [defaultValues, setDefaultValues] = useState<any>();
  const { isDirty, isSubmitSuccessful } = formState;
  const { fields, prepend, remove } = useFieldArray({
    control,
    name: SmartSuggestionSettingUseFieldName
  });
  const {
    putSmartSuggestionSettings,
    loading: saving
  } = usePutSmartSuggestionSettings();
  const handleClick = useCallback(() => {
    if (enabledGroupTypes && enabledGroupTypes.length) {
      const values = enabledGroupTypes.reduce((a, c) => {
        a[c.value] = [];
        return a;
      }, {} as Record<GroupType, Array<any>>);
      prepend({
        ...values,
        ...{
          enableDisabled: EnabledDisabled.enabled
        }
      });
    }
  }, [enabledGroupTypes, prepend]);
  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({ ...defaultValues });
    }
  }, [reset, defaultValues, isSubmitSuccessful]);
  useEffect(() => {
    reset(getValues());
  }, [getValues, reset]);
  const onSubmit = useCallback(
    (formData) => {
      onClose?.();
      const saveSettings = async () => {
        let settings: SmartSuggestionSettingsInput[] = [];
        if (formData[SmartSuggestionSettingUseFieldName]) {
          settings = formData[SmartSuggestionSettingUseFieldName].map(
            (r: any) => {
              return {
                enabled: r.enableDisabled === EnabledDisabled.enabled,
                accessScopeIn: {
                  ...mapToAccessSope('departmentGroups', r.DEPARTMENT),
                  ...mapToAccessSope('employeeTypeGroups', r.EMPLOYEE_TYPE),
                  ...mapToAccessSope('locations', r.LOCATION),
                  ...mapToAccessSope('costCenters', r.COST_CENTER),
                  ...mapToAccessSope('divisions', r.DIVISION),
                  ...mapToAccessSope('serviceCenters', r.SERVICE_CENTER)
                }
              };
            }
          );
        }
        settings = settings.concat({
          enabled:
            formData.defaultRow.enableDisabled === EnabledDisabled.enabled
        });
        await putSmartSuggestionSettings({ settings: settings });
      };
      const showAlert = () => {
        setShow(false);
        setShow(true);
      };
      try {
        saveSettings();
        setDefaultValues(formData);
      } catch {
        setSeverity?.('error');
      }
      isTiAutoAcceptUserSettingsEnabled ? setAlert?.(true) : showAlert();
    },
    [
      isTiAutoAcceptUserSettingsEnabled,
      onClose,
      putSmartSuggestionSettings,
      setAlert,
      setSeverity
    ]
  );
  const handleRemove = useCallback(
    (rowId) => {
      remove(rowId);
    },
    [remove]
  );
  return (
    //to fix type instantiation is excessively deep and infinite
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit(onSubmit)}
        className={isTiAutoAcceptUserSettingsEnabled ? classes.form : undefined}
      >
        <SettingBody
          enabledGroupTypes={enabledGroupTypes}
          fields={fields}
          show={show}
          category={category}
          message={message}
          isDirty={isDirty}
          handleClick={handleClick}
          handleRemove={handleRemove}
          saving={saving}
          fieldName={SmartSuggestionSettingUseFieldName}
          defaultRowValue={fieldValues.defaultValues.defaultEnabled}
          onClose={onClose}
        />
      </form>
    </FormProvider>
  );
};
